import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { invitationsAPI, carpoolsAPI } from '../services/api';
import Modal from './Modal';
import './InviteModal.css';

const InviteModal = ({ isOpen, onClose, carpoolId, carpoolName, onInviteSent }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('email');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [pendingInvitations, setPendingInvitations] = useState([]);
  const [copied, setCopied] = useState(false);
  const [inviteLink, setInviteLink] = useState('');
  const [linkLoading, setLinkLoading] = useState(false);

  useEffect(() => {
    if (isOpen && carpoolId) {
      fetchPendingInvitations();
      // Reset link when modal opens
      setInviteLink('');
    }
  }, [isOpen, carpoolId]);

  const fetchPendingInvitations = async () => {
    try {
      const response = await carpoolsAPI.getInvitations(carpoolId);
      setPendingInvitations(response.data.invitations || []);
    } catch (err) {
      console.error('Failed to fetch invitations:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const data = { carpoolId };
      if (activeTab === 'email' && email) {
        data.receiverEmail = email;
      } else if (activeTab === 'phone' && phone) {
        data.receiverPhone = phone;
      } else {
        setError(t('invitations.inviteModal.errorEmailOrPhone'));
        setLoading(false);
        return;
      }

      const response = await invitationsAPI.sendInvitation(data);

      if (response.data.reminder) {
        setSuccess('Напомняне изпратено успешно!');
      } else {
        setSuccess('Поканата е изпратена успешно!');
      }

      setEmail('');
      setPhone('');
      fetchPendingInvitations();
      onInviteSent?.();
    } catch (err) {
      setError(err.response?.data?.error?.message || t('invitations.inviteModal.errorSendFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleCancelInvitation = async (invitationId) => {
    try {
      await invitationsAPI.cancelInvitation(invitationId);
      fetchPendingInvitations();
    } catch (err) {
      setError(err.response?.data?.error?.message || t('invitations.inviteModal.errorCancelFailed'));
    }
  };

  const handleResendReminder = async (invitationId) => {
    try {
      await invitationsAPI.sendReminder(invitationId);
      setSuccess(t('invitations.invitationReminderSent'));
      fetchPendingInvitations();
    } catch (err) {
      setError(err.response?.data?.error?.message || t('invitations.inviteModal.errorReminderFailed'));
    }
  };

  const generateInviteLink = async () => {
    if (inviteLink) return inviteLink;

    try {
      setLinkLoading(true);
      setError('');
      const response = await invitationsAPI.createInviteLink(carpoolId);
      const token = response.data.invitation.token;
      const baseUrl = window.location.origin;
      const link = `${baseUrl}/join/${token}`;
      setInviteLink(link);
      return link;
    } catch (err) {
      console.error('Failed to generate invite link:', err);
      setError(err.response?.data?.error?.message || t('invitations.inviteModal.errorLinkGenerateFailed'));
      return '';
    } finally {
      setLinkLoading(false);
    }
  };

  const handleCopyLink = async () => {
    try {
      let link = inviteLink;
      if (!link) {
        link = await generateInviteLink();
        if (!link) return;
      }
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      setError(t('invitations.inviteModal.errorCopyFailed'));
    }
  };

  // Generate link when switching to link tab
  useEffect(() => {
    if (activeTab === 'link' && !inviteLink && carpoolId) {
      generateInviteLink();
    }
  }, [activeTab, carpoolId]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('invitations.inviteModal.title')} size="medium">
      <div className="invite-modal">
        {/* Tabs */}
        <div className="invite-tabs">
          <button
            className={`invite-tab ${activeTab === 'email' ? 'active' : ''}`}
            onClick={() => setActiveTab('email')}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
              <polyline points="22,6 12,13 2,6" />
            </svg>
            {t('invitations.inviteModal.tabEmail')}
          </button>
          <button
            className={`invite-tab ${activeTab === 'phone' ? 'active' : ''}`}
            onClick={() => setActiveTab('phone')}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
            </svg>
            {t('invitations.inviteModal.tabPhone')}
          </button>
          <button
            className={`invite-tab ${activeTab === 'link' ? 'active' : ''}`}
            onClick={() => setActiveTab('link')}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
              <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
            </svg>
            {t('invitations.inviteModal.tabLink')}
          </button>
        </div>

        {/* Messages */}
        {error && <div className="invite-error">{error}</div>}
        {success && <div className="invite-success">{success}</div>}

        {/* Tab content */}
        <div className="invite-content">
          {activeTab === 'email' && (
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="email">{t('invitations.inviteModal.emailLabel')}</label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="example@email.com"
                  required
                />
              </div>
              <button type="submit" className="btn-primary" disabled={loading || !email}>
                {loading ? t('invitations.inviteModal.sending') : t('invitations.inviteModal.sendInvite')}
              </button>
            </form>
          )}

          {activeTab === 'phone' && (
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="phone">{t('invitations.inviteModal.phoneLabel')}</label>
                <input
                  type="tel"
                  id="phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+359 888 123 456"
                  required
                />
              </div>
              <button type="submit" className="btn-primary" disabled={loading || !phone}>
                {loading ? t('invitations.inviteModal.sending') : t('invitations.inviteModal.sendInvite')}
              </button>
            </form>
          )}

          {activeTab === 'link' && (
            <div className="link-invite">
              <p className="link-description">
                {t('invitations.inviteModal.linkDescription', { carpoolName: carpoolName || '' })}
              </p>
              {linkLoading ? (
                <div className="link-loading">
                  <div className="link-spinner" />
                  <span>{t('invitations.inviteModal.generatingLink')}</span>
                </div>
              ) : (
                <>
                  <div className="link-box">
                    <input
                      type="text"
                      value={inviteLink || t('invitations.inviteModal.linkErrorFallback')}
                      readOnly
                      placeholder={t('invitations.inviteModal.linkPlaceholder')}
                    />
                    <button
                      className="copy-btn"
                      onClick={handleCopyLink}
                      disabled={!inviteLink}
                    >
                      {copied ? (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      ) : (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                        </svg>
                      )}
                    </button>
                  </div>
                  {copied && <span className="copied-message">{t('invitations.inviteModal.copied')}</span>}
                  <p className="link-hint">{t('invitations.inviteModal.linkHintValid30Days')}</p>
                </>
              )}
            </div>
          )}
        </div>

        {/* Pending invitations */}
        {pendingInvitations.length > 0 && (
          <div className="pending-invitations">
            <h4>{t('invitations.inviteModal.pendingTitle', { count: pendingInvitations.length })}</h4>
            <div className="invitation-list">
              {pendingInvitations.map((inv) => (
                <div key={inv.id} className="invitation-item">
                  <div className="invitation-info">
                    <span className="invitation-contact">
                      {inv.receiverEmail || inv.receiverPhone}
                    </span>
                    <span className="invitation-date">
                      {new Date(inv.sentAt).toLocaleDateString('bg-BG')}
                    </span>
                  </div>
                  <div className="invitation-actions">
                    <button
                      className="btn-icon"
                      onClick={() => handleResendReminder(inv.id)}
                      title={t('invitations.inviteModal.reminderTitle')}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="23 4 23 10 17 10" />
                        <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
                      </svg>
                    </button>
                    <button
                      className="btn-icon danger"
                      onClick={() => handleCancelInvitation(inv.id)}
                      title={t('invitations.inviteModal.cancelTitle')}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default InviteModal;
